import React, { useState, useEffect, useMemo } from "react";
import { Box, Typography, Grid, Button } from "@mui/material";
import PlayerCard from "../../components/PlayerCard";
import defaultProfile from "../../assets/images/defaultProfile2.webp";
import PlayerStatsModal from "../../components/Modal/PlayerStatsModal";
import FilterSidebar from "../../components/FilterSidebar";
import FilterListIcon from "@mui/icons-material/FilterList";
import { useLeaderBoardMutation } from "../../services/tournamentsAPI";
import NoDataView from "../../components/NoDataView";
import { useGetAllRolesQuery } from "../../services/auctionAPI";

const AuctionPlayers = ({ data, search }) => {
  const [openModal, setOpenModal] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState(null);

  const [openFilter, setOpenFilter] = useState(false);

  // FINAL FILTERS
  const [selectedRoles, setSelectedRoles] = useState([]); // stores role IDs
  const [selectedTeams, setSelectedTeams] = useState([]);

  // TEMPORARY FILTERS
  const [tempRoles, setTempRoles] = useState([]);
  const [tempTeams, setTempTeams] = useState([]);

  const auctionTeams = data?.auctionDetails?.auctionTeams || [];
  const teamMembers = data?.teamMembers || [];
  const playingMembers = data?.playingMembers || [];

  const { data: rolesData } = useGetAllRolesQuery();
  const roles = rolesData?.data || []; // API roles

  // Extract ID safely
  const extractId = (idOrObj) =>
    typeof idOrObj === "string"
      ? idOrObj
      : idOrObj?._id || idOrObj?.roleId || idOrObj?.teamId;

  // Map: API roles → { id, name }
  const rolesList = useMemo(() => {
    return roles.map((r) => ({
      id: String(r._id),
      name: r.name,
    }));
  }, [roles]);

  const teamsList = data?.teams || [];
  const getTeamName = (idOrObj) => {
    const id = extractId(idOrObj);
    return teamsList.find((t) => t._id === id)?.teamName || "Unknown Team";
  };

  // Enrich players with teamId and serialNo
  const enrichedPlayers = useMemo(() => {
    if (teamMembers && teamMembers.length > 0) {
      return teamMembers.map((tm, index) => {
        const pmMatch = playingMembers.find(
          (pm) => pm._id === tm.playerId?._id || pm._id === tm._id
        );
        return {
          ...pmMatch,
          ...tm,
          _id: tm.playerId?._id || tm._id,
          teamMemberId: tm._id,
          name: tm.playerName || tm.name || pmMatch?.name,
          roleId:
            tm.battingRoleId ||
            tm.roleId ||
            tm.playerId?.roleId ||
            pmMatch?.roleId,
          teamId: tm.teamId,
          title: tm.title || pmMatch?.title,
          playerId: tm.playerId || pmMatch?.playerId || pmMatch,
          serialNo: tm.serialNo ?? pmMatch?.serialNo ?? index + 1,
        };
      });
    }

    return playingMembers.map((pm, index) => {
      const match = teamMembers.find(
        (tm) => tm.playerId?._id === pm._id || tm._id === pm._id
      );
      return {
        ...pm,
        teamId: match?.teamId,
        title: match?.title,
        playerId: match?.playerId || pm.playerId,
        serialNo: match?.serialNo ?? pm.serialNo ?? index + 1,
      };
    });
  }, [teamMembers, playingMembers]);

  const safeSearch = String(search || "")
    .toLowerCase()
    .trim();

  // Unique teams list
  const uniqueTeams = useMemo(() => {
    const list = auctionTeams.map((t) => getTeamName(t.teamId));
    return [...new Set(list.filter(Boolean))];
  }, [auctionTeams, teamsList]);

  // === FINAL FILTERING USING ROLE IDs & SEARCH (NAME & SERIAL NO) ===
  const filteredPlayers = useMemo(() => {
    return enrichedPlayers.filter((player, index) => {
      // Search by name or serialNo
      const playerName = (
        player?.name ||
        player?.playerName ||
        player?.playerId?.name ||
        ""
      ).toLowerCase();

      const isNumeric = !isNaN(safeSearch) && !isNaN(parseFloat(safeSearch));

      // Name matches if name contains safeSearch
      const nameMatch = playerName.includes(safeSearch);

      // SerialNo matches:
      // If user typed a number (e.g. 1), match exact serial number
      const playerSerialNo = player?.serialNo ?? index + 1;
      const serialMatch = isNumeric
        ? Number(playerSerialNo) === Number(safeSearch)
        : String(playerSerialNo).toLowerCase() === safeSearch;

      const searchMatch = !safeSearch || nameMatch || serialMatch;

      // ROLE MATCH BASED ON ID
      const roleId = String(extractId(player.roleId));
      const roleMatch =
        selectedRoles.length === 0 || selectedRoles.includes(roleId);

      // TEAM MATCH
      const teamName = getTeamName(player.teamId);
      const teamMatch =
        selectedTeams.length === 0 || selectedTeams.includes(teamName);

      return searchMatch && roleMatch && teamMatch;
    });
  }, [enrichedPlayers, safeSearch, selectedRoles, selectedTeams]);

  // APPLY FILTERS
  const applyFilters = () => {
    setSelectedRoles(tempRoles); // IDs
    setSelectedTeams(tempTeams);
    setOpenFilter(false);
  };

  const resetFilters = () => {
    setTempRoles([]);
    setTempTeams([]);

    // Reset applied filters instantly
    setSelectedRoles([]);
    setSelectedTeams([]);
    setOpenFilter(false);
  };

  const handleOpenFilter = () => {
    setTempRoles(selectedRoles);
    setTempTeams(selectedTeams);
    setOpenFilter(true);
  };

  // Leaderboard fetch
  const [getLeaderboard, { data: leaderboardData, isLoading }] =
    useLeaderBoardMutation();

  useEffect(() => {
    if (!data?._id) return;

    getLeaderboard({
      criteria: "most_wickets",
      tournamentId: data._id,
      allTeams: true,
      page: "1",
      limit: "200",
    });
  }, [data?._id]);

  return (
    <Box sx={{ px: "3rem", py: "1.5rem" }}>
      {/* HEADER */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: "1.5rem",
          gap: 2,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Total Players: {filteredPlayers.length}
        </Typography>

        <Button
          variant="contained"
          startIcon={<FilterListIcon />}
          sx={{
            backgroundColor: "#212121",
            color: "#fff",
            borderRadius: 6,
            fontSize: "20px",
            textTransform: "none",
            px: 2.5,
            py: 0.7,
          }}
          onClick={handleOpenFilter}
        >
          Filter
        </Button>
      </Box>

      {/* PLAYERS GRID */}
      <Grid container columnSpacing={2} rowSpacing={3}>
        {filteredPlayers.length > 0 ? (
          filteredPlayers.map((player) => (
            <Grid item xs={12} sm={6} md={3} key={player._id}>
              <PlayerCard
                name={player.name || player.playerName}
                role={player.roleId}
                image={player?.playerId?.profilePic || defaultProfile}
                onView={() => {
                  setSelectedPlayer(player);
                  setOpenModal(true);
                }}
                showActions={true}
                showPurchase={false}
                isCaptain={player?.title.includes("Captain")}
                isIconPlayer={player?.title.includes("Icon Player")}
                onClick={() => {
                  setSelectedPlayer(player);
                  setOpenModal(true);
                }}
              />
            </Grid>
          ))
        ) : (
          <NoDataView text="No players found" />
        )}
      </Grid>

      <PlayerStatsModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        player={selectedPlayer}
        leaderboard={leaderboardData?.data?.players || []}
        loading={isLoading}
      />

      {/* FILTER SIDEBAR */}
      <FilterSidebar
        open={openFilter}
        onClose={() => setOpenFilter(false)}
        roles={rolesList} // {id, name}
        teams={uniqueTeams} // ["Team A", "Team B"]
        tempRoles={tempRoles} // IDs
        tempTeams={tempTeams}
        setTempRoles={setTempRoles}
        setTempTeams={setTempTeams}
        onApplyFilters={applyFilters}
        onResetFilters={resetFilters}
      />
    </Box>
  );
};

export default AuctionPlayers;
